// src/pages/Home.js
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Hero from "../components/Hero";
import ProductGrid from "../components/ProductGrid";
import Pagination from "../components/Pagination";
import { HOME_COLLECTIONS, SITE, createWhatsAppLink } from "../config/site";

const PAGE_SIZE = 16;

/* ---------- Env + endpoints (mirror AdminProducts.js) ---------- */
const IS_LOCAL =
  typeof window !== "undefined" &&
  /localhost|127\.0\.0\.1/i.test(window.location.hostname);

const API_BASE = IS_LOCAL
  ? "http://localhost/shop/routes"
  : "https://shop.petrahcollection.co.ke/shop/routes";

const CATALOG_API = `${API_BASE}/products_petro.php?action=catalog`;
const META_API    = `${API_BASE}/products-meta.php`;

/* Build absolute URL for images like "/images/...", "/public/...", "/shop/routes/uploads" */
const PHP_ORIGIN = new URL(API_BASE).origin;
const APP_ORIGIN =
  typeof window !== "undefined" ? window.location.origin : PHP_ORIGIN;

const toAbs = (u) => {
  if (!u) return u;
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/images"))              return APP_ORIGIN + u;   // React /public
  if (u.startsWith("/public/"))             return PHP_ORIGIN + u;   // Apache htdocs
  if (u.startsWith("/shop/routes/uploads")) return PHP_ORIGIN + u;   // PHP uploads
  return new URL(u, PHP_ORIGIN).href;
};

/* Safe JSON fetch that tolerates stray output before JSON */
async function safeJsonFetch(input, init) {
  const res = await fetch(input, {
    headers: { Accept: "application/json" },
    ...init,
  });
  const text = await res.text();
  let json = null;
  try {
    // Be a bit looser: some endpoints wrap as {ok,data}
    const start = text.search(/[[]|[{]|"ok"|"data"/);
    const body = start >= 0 ? text.slice(start) : text;
    json = body ? JSON.parse(body) : null;
  } catch {}
  if (!res.ok) {
    const msg =
      json?.error ||
      json?.message ||
      text.slice(0, 160).replace(/\s+/g, " ").trim() ||
      `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return json ?? {};
}

/* ---------- Helpers ---------- */
const norm = (s) => (s ?? "").toString().trim();

/* ---------- Page ---------- */
export default function Home({ onAdd }) {
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [loading, setLoad] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoad(true);
        setError("");

        // Fetch catalog + meta in parallel
        const [catalogRaw, metaRaw] = await Promise.all([
          safeJsonFetch(CATALOG_API, { credentials: "include" }),
          safeJsonFetch(META_API).catch(() => ([])), // meta is optional
        ]);

        const catalog = Array.isArray(catalogRaw) ? catalogRaw : catalogRaw?.data || [];
        const metaArr = Array.isArray(metaRaw) ? metaRaw : metaRaw?.data || [];

        // Map meta by (product_name + color) for exact variant match
        const metaByPair = new Map(
          metaArr
            .filter((m) => m?.product_name != null && m?.color != null)
            .map((m) => [
              `${norm(m.product_name)}__${norm(m.color)}`,
              m,
            ])
        );

        // Merge: only apply meta to the SAME (name, color)
        const merged = catalog.map((p) => {
          const name  = norm(p?.name);
          const color = norm(p?.color ?? "");
          const key   = `${name}__${color}`;
          const m     = metaByPair.get(key);

          const image = toAbs(m?.image ?? p?.image ?? "");
          // ProductCard reads product.price (not display_price)
          const price = m?.display_price ?? p?.price ?? p?.display_price ?? null;

          return { ...p, name, color, image, price };
        });

        // Sort: products with pictures first, then alphabetically
        merged.sort((a, b) => {
          const hasA = a.image ? 1 : 0;
          const hasB = b.image ? 1 : 0;
          if (hasA !== hasB) return hasB - hasA; // images first
          const n = String(a.name).localeCompare(String(b.name));
          return n || String(a.color).localeCompare(String(b.color));
        });

        if (mounted) setItems(merged);
      } catch (e) {
        if (mounted) setError(e.message || "Failed to load products");
      } finally {
        if (mounted) setLoad(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));

  // keep page in bounds when items change
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return items.slice(start, start + PAGE_SIZE);
  }, [items, page]);

  return (
    <div className="mx-auto max-w-7xl px-2 sm:px-4">
      <div className="pt-3 sm:pt-4" />
      <Hero />

      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <Feature
          icon={<TruckIcon className="h-6 w-6" />}
          title="Fast order support"
          desc="Quick delivery coordination and responsive updates through WhatsApp."
        />
        <Feature
          icon={<CashIcon className="h-6 w-6" />}
          title="Flexible payment"
          desc="Confirm your order first, then pay via cash, M-Pesa, bank, or WhatsApp."
        />
        <Feature
          icon={<HeadsetIcon className="h-6 w-6" />}
          title="Visit our shop"
          desc={`${SITE.locationTitle} - ${SITE.locationLine}`}
        />
      </div>

      <section className="mt-8 rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#e46a2e]">Explore the store</p>
            <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-950">Find the right pair faster</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
              Browse collections quickly, then use the contact, FAQ, and support pages whenever you need store details, location help, or order guidance.
            </p>
          </div>
          <Link
            to="/contact-us"
            className="inline-flex items-center justify-center rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-[#e46a2e] hover:text-[#e46a2e]"
          >
            Visit contact page
          </Link>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {HOME_COLLECTIONS.map((item) => (
            <Link
              key={item.title}
              to={item.to}
              className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5 transition hover:-translate-y-0.5 hover:border-[#e46a2e] hover:bg-orange-50"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-[#e46a2e] shadow-sm">
                <ArrowIcon />
              </div>
              <h3 className="mt-4 text-lg font-bold text-slate-950">{item.title}</h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">{item.description}</p>
            </Link>
          ))}
        </div>
      </section>

      <div className="mt-8 flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#e46a2e]">Featured picks</p>
          <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-950">Trending right now</h2>
        </div>
        <a
          href={createWhatsAppLink(`Hi Petrah Collection, I would like help choosing a pair from the featured products.`)}
          target="_blank"
          rel="noreferrer"
          className="hidden rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-[#e46a2e] hover:text-[#e46a2e] sm:inline-flex"
        >
          Need help choosing?
        </a>
      </div>

      <div className="mt-4">
        <ProductGrid items={pageItems} loading={loading} error={error} onAdd={onAdd} />
      </div>

      <div className="mt-6 mb-8">
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      </div>
    </div>
  );
}

/* ---------- UI bits ---------- */
function Feature({ icon, title, desc }) {
  return (
    <div className="rounded-md border border-[#e4e7ea] bg-white px-4 py-3 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="mt-[2px] text-[#d24a17]">{icon}</div>
        <div>
          <div className="text-sm font-semibold text-[#263238]">{title}</div>
          <div className="text-xs text-[#6b7280]">{desc}</div>
        </div>
      </div>
    </div>
  );
}

function TruckIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
      <path strokeWidth="1.5" d="M3 6h11v8H3zM14 9h3l3 3v2h-6z" />
      <circle cx="7" cy="17" r="2" />
      <circle cx="17" cy="17" r="2" />
    </svg>
  );
}
function CashIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
      <rect x="3" y="6" width="18" height="12" rx="2" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="2.25" />
    </svg>
  );
}
function HeadsetIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
      <path
        strokeWidth="1.5"
        d="M4 12a8 8 0 1116 0v6a2 2 0 01-2 2h-3v-6h5M4 12v6a2 2 0 002 2h3v-6H4"
      />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-5 w-5">
      <path strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="M7 17L17 7M8 7h9v9" />
    </svg>
  );
}
